import {
  catalogueDeviations,
  catalogueVersionInForce,
  isOnceAWeek,
  type MedicationCatalogue,
  type MedicationCatalogueProduct,
} from "@care-by-ella/contracts";

// TES-1292 — the order picker's rules, apart from the component so they can
// be tested with `node --test`.
//
// The rollout rule (contracts `catalogueVersionInForce`): a product's picker
// is the required path only once its current version is clinically approved
// and lists strengths and intervals. Before that the product's dose and
// interval stay free text, with no motivation asked.

export type PickerDraft = {
  medication: string;
  dose: string;
  frequency: string;
  medicationVersionId?: string;
};

const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/**
 * The catalogue product the draft names: by its version reference, else by
 * the medication's name (an order typed or saved before the catalogue, or
 * one saved against a version that has since been replaced).
 */
export function productOf(
  catalogue: MedicationCatalogue | null,
  draft: Pick<PickerDraft, "medication" | "medicationVersionId">,
): MedicationCatalogueProduct | null {
  if (!catalogue) return null;
  if (draft.medicationVersionId) {
    const byId = catalogue.products.find(
      (p) => p.current.id === draft.medicationVersionId,
    );
    if (byId) return byId;
  }
  const name = norm(draft.medication);
  if (!name) return null;
  return catalogue.products.find((p) => norm(p.name) === name) ?? null;
}

export function productInForce(p: MedicationCatalogueProduct | null): boolean {
  return p !== null && catalogueVersionInForce(p.current);
}

/** Is any product's picker in force? Then a medication outside it deviates. */
export function catalogueInForce(
  catalogue: MedicationCatalogue | null,
): boolean {
  return (catalogue?.products ?? []).some((p) => productInForce(p));
}

/**
 * Does the draft need the doctor's motivation? Only for a deviation from a
 * product in force, or for a medication outside the catalogue once any
 * product is in force. Never before an approval.
 */
export function deviationRequired(
  catalogue: MedicationCatalogue | null,
  draft: PickerDraft,
): boolean {
  const product = productOf(catalogue, draft);
  if (product) {
    return (
      catalogueDeviations(
        {
          name: product.name,
          clinicallyApprovedAt: product.current.clinicallyApprovedAt,
          strengths: product.current.strengths,
          intervals: product.current.intervals,
        },
        draft,
      ).length > 0
    );
  }
  return catalogueInForce(catalogue) && norm(draft.medication) !== "";
}

/**
 * The version reference the draft should carry: the matched product's current
 * version, or none for a medication outside the catalogue.
 */
export function linkedVersionId(
  catalogue: MedicationCatalogue | null,
  draft: Pick<PickerDraft, "medication" | "medicationVersionId">,
): string | undefined {
  if (!catalogue) return draft.medicationVersionId;
  return productOf(catalogue, draft)?.current.id;
}

/**
 * TES-1375 (Ö-419) — the dose intervals the order may be given: once a week
 * only, so the field is a fixed choice and never free text. An in-force
 * entry offers its own weekly spelling (so the order does not read as a
 * deviation from it); otherwise "en gång i veckan". A weekly spelling the
 * draft already carries (an older order, prefilled) is kept as an option so
 * a renewal does not silently reword what the patient reads; outside an
 * in-force entry it is a deviation like any other. A non-weekly value is
 * never offered: the doctor has to choose again.
 */
export function intervalOptions(
  product: MedicationCatalogueProduct | null,
  current: string,
  fallback: string,
): string[] {
  const fromEntry =
    product && productInForce(product)
      ? product.current.intervals.filter((i) => isOnceAWeek(i))
      : [];
  const base = fromEntry.length > 0 ? fromEntry : [fallback];
  const typed = current.trim();
  return typed && !base.includes(typed) && isOnceAWeek(typed)
    ? [...base, typed]
    : base;
}
